import {
  collection, addDoc, getDocs, updateDoc,
  query, where, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';
import type {
  IPasswordResetRepository, CreatePasswordResetData, GetPasswordResetData,
} from './interfaces/password-reset.repository.interface';

export class PasswordResetRepository implements IPasswordResetRepository {
  private collectionName = 'passwordResets';

  async createPasswordReset(resetData: CreatePasswordResetData): Promise<GetPasswordResetData> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...resetData,
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 2 * 60 * 60 * 1000), // 2 hours
      });
      return {
        id: docRef.id,
        ...resetData,
        expiresAt: new Date(Date.now() + 2 * 60 * 60 * 1000),
        createdAt: new Date(),
      } as unknown as GetPasswordResetData;
    } catch (error) {
      throw new DRXError({
        code: 'PASSWORD_RESET_CREATE_FAILED',
        devMessage: 'Failed to create password reset',
        cause: error,
      });
    }
  }

  async verifyPasswordReset(token: string): Promise<{ success: boolean; email: string; userId: string }> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('token', '==', token),
        where('usedAt', '==', null),
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new DRXError({
          code: 'PASSWORD_RESET_INVALID_TOKEN',
          devMessage: `Invalid or expired reset token: ${token}`,
          userMessage: 'Invalid or expired reset link.',
          level: 'warning',
        });
      }

      const resetDoc = snapshot.docs[0];
      const resetData = resetDoc.data();

      if (resetData.expiresAt.toDate() < new Date()) {
        throw new DRXError({
          code: 'PASSWORD_RESET_EXPIRED',
          devMessage: `Reset token expired: ${token}`,
          userMessage: 'This reset link has expired. Please request a new one.',
          level: 'warning',
        });
      }

      return { success: true, email: resetData.email, userId: resetData.userId };
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'PASSWORD_RESET_VERIFY_FAILED',
        devMessage: `Failed to verify password reset token ${token}`,
        cause: error,
      });
    }
  }

  async usePasswordReset(token: string, _newPassword: string): Promise<{ success: boolean; email: string; userId: string }> {
    try {
      const verification = await this.verifyPasswordReset(token);

      // Mark token as used
      const q = query(
        collection(db, this.collectionName),
        where('token', '==', token),
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        await updateDoc(snapshot.docs[0].ref, { usedAt: serverTimestamp() });
      }

      return verification;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'PASSWORD_RESET_USE_FAILED',
        devMessage: `Failed to use password reset token ${token}`,
        cause: error,
      });
    }
  }
}
